import type { PullPlan, PullPlanWBSNode } from "../../../models";
import { parseDraftPayload } from "../../../models";
import type { SectionCardSummary } from "./summarizeSection";

/** A plain (non-Takt) plan rendered as a section card. */
export interface PlainPlanCard {
  /** The owning pull_plan id (the open + card key). */
  draftId: number;
  /** WBS root name this plan groups under. */
  groupName: string;
  summary: SectionCardSummary;
  /** The WBS node id to anchor the open on (the plan's section_wbs_id). */
  anchorWbsId: number;
}

/** Walk a node up to its depth-0 ancestor's name, or its own when already a
 *  root / orphaned. */
export function wbsRootName(
  nodeId: number,
  byId: Map<number, PullPlanWBSNode>,
): string {
  let cur = byId.get(nodeId);
  if (!cur) return "Other";
  // Guard against cycles in malformed trees.
  const seen = new Set<number>();
  while (cur.parent_id != null && byId.has(cur.parent_id) && !seen.has(cur.id)) {
    seen.add(cur.id);
    cur = byId.get(cur.parent_id)!;
  }
  return cur.name;
}

/** Build section-card models for every PLAIN plan (payload kind === "plain").
 *  Takt plans are skipped (they ride the Takt-section spine). */
export function plainPlanCards(
  plans: PullPlan[],
  tree: PullPlanWBSNode[] | null,
): PlainPlanCard[] {
  const byId = new Map<number, PullPlanWBSNode>();
  if (tree) for (const n of tree) byId.set(n.id, n);

  const out: PlainPlanCard[] = [];
  for (const plan of plans) {
    const payload = parseDraftPayload(plan.payload_json);
    if (payload.kind !== "plain") continue;

    const scope = payload.scope_wbs_ids.length
      ? payload.scope_wbs_ids
      : [plan.section_wbs_id];
    const wbsNodes = scope.map((id) => ({
      id,
      name: byId.get(id)?.name ?? null,
    }));
    // Real activity total from the WBS tree's per-leaf task counts (what the
    // New-plan panel sums) — not the leaf count, which the "N activities" chip
    // would otherwise mislabel.
    const activityTotal = scope.reduce(
      (sum, id) => sum + (byId.get(id)?.counts.tasks ?? 0),
      0,
    );

    out.push({
      draftId: plan.id,
      groupName: wbsRootName(plan.section_wbs_id, byId),
      anchorWbsId: plan.section_wbs_id,
      summary: {
        name: plan.name,
        taktA: "",
        sortA: 0,
        wbsRoot: null,
        activityCount: activityTotal,
        workCodeCount: 0,
        zoneCount: 0,
        earliestStart: null,
        latestFinish: null,
        workdayCount: 0,
        wbsNodes,
        scopeMemberCount: activityTotal,
        taggedCount: 0,
        untaggedCount: 0,
        scopeOverlaps: [],
      },
    });
  }
  return out;
}
